import React from 'react';
import { AutoRefreshToolbar } from '@/components/common';
import { TruncatedCell } from '@/components/table';
import type { AutoRefreshControl } from '@/hooks/useAutoRefresh';
import type { DiagnosticsData } from '@/types';

function formatCheckedAt(ts: number | Date | undefined | null): string {
  if (ts == null) return '—';
  const ms = ts instanceof Date ? ts.getTime() : ts * 1000;
  return new Date(ms).toLocaleString();
}

function severityBadgeClass(state: string | undefined, severity: string): string {
  const normalized = (state || severity || '').toLowerCase();
  if (normalized === 'healthy' || normalized === 'ok') return 'badge-ok';
  if (normalized === 'degraded' || normalized === 'warning') return 'badge-degraded';
  if (normalized === 'misconfigured') return 'badge-warn';
  if (normalized === 'unavailable' || normalized === 'critical') return 'badge-bad';
  return 'badge-muted';
}

function severityLabel(state: string | undefined, severity: string): string {
  if (state) {
    return state.charAt(0).toUpperCase() + state.slice(1);
  }
  return severity;
}

function actionHint(state: string | undefined, severity: string, name: string): string | null {
  const key = (state || severity || '').toLowerCase();
  if (key === 'healthy' || key === 'ok') return null;
  if (key === 'misconfigured') {
    if (name.includes('telegram')) return 'Configure Telegram bot token or run Telethon auth.';
    if (name.includes('online_payments')) return 'Configure an enabled payment provider or use manual billing.';
    return 'Review configuration for this check.';
  }
  if (key === 'degraded' || key === 'warning') {
    return 'Monitor closely; core services may still be running.';
  }
  return 'Investigate dependency connectivity and worker logs.';
}

type Props = {
  diagnostics: DiagnosticsData | null;
  loading: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
  autoRefresh: AutoRefreshControl;
  autoRefreshing: boolean;
};

export default function DiagnosticsPanel({
  diagnostics,
  loading,
  error,
  lastUpdatedAt,
  autoRefresh,
  autoRefreshing,
}: Props) {
  const checkedLabel = diagnostics?.checked_at
    ? formatCheckedAt(diagnostics.checked_at)
    : formatCheckedAt(lastUpdatedAt);
  const showTable = diagnostics && diagnostics.checks.length > 0;

  return (
    <>
      <div className="auto-refresh-section__header diagnostics-panel__header">
        <div>
          <p className="muted diagnostics-updated">
            Runtime checks and worker health
            {autoRefreshing ? ' · refreshing…' : ''}
          </p>
          <p className="muted diagnostics-updated">
            Last checked: {checkedLabel}
            {diagnostics?.request_id ? ` · Request ${diagnostics.request_id.slice(0, 8)}` : ''}
          </p>
        </div>
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 12s" />
      </div>

      {error ? <p className="alert alert-error">{error}</p> : null}
      {!showTable && loading ? <p className="muted">Loading diagnostics…</p> : null}
      {!showTable && !loading && !error ? <p className="muted">No diagnostic checks available.</p> : null}

      {showTable ? (
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Check</th>
                <th>Status</th>
                <th>Message</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {diagnostics.checks.map((c) => {
                const hint = actionHint(c.state, c.severity, c.name);
                return (
                  <tr key={c.name}>
                    <td><TruncatedCell value={c.name} /></td>
                    <td>
                      <span className={`badge ${severityBadgeClass(c.state, c.severity)}`}>
                        {severityLabel(c.state, c.severity)}
                      </span>
                    </td>
                    <td><TruncatedCell value={c.message} /></td>
                    <td className="muted">{hint ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
